import {
  type PaymentManagePayeesPageParams,
  type PaymentManagePayeesPageTab,
} from '@webapp/pages/PaymentManagePayeesPage'
import { type PageUrlsBuilders } from './types'

/**
 * Rules:
 *
 * Each key within this object must be named the same as the
 * page that will be rendered.
 */
export const PageUrls: PageUrlsBuilders = {
  AdminToolsPage: ({ tool }) => `/admin-tools/${tool}`,
  BillApprovalDetailsPage: ({ uuid }) => `/bills/approvals/${uuid}`,
  BillApprovalListPage: () => `/#/bills/approvals`,
  BillApprovalsPage: ({ status }) => `/bills/approvals/${status}`,
  BillCreatePage: ({ uuid = '' }) => `/bills/create/${uuid}`,
  BillListPage: ({ status }) => `/bills/requests/${status}`,
  BillRequestDetailsPage: ({ uuid }) => `/bills/requests/${uuid}`,
  CreditCardReconciliationPage: () => '/ap/reconcile',
  CreditCardAccountDetailsPage: ({ id, tab }) => `/ap/reconcile/${id}/${tab}`,
  ReconciliationMatchPage: ({ id, tab }) => `/ap/reconcile/${id}/match/${tab}`,
  CreateCreditCardBillPage: ({ id }) => `/ap/reconcile/${id}/match/create`,
  ExpenseRecordsPage: ({ type }) => `/ap/expense-records/${type}`,
  ContractCreatePage: ({ uuid = '' }) => `/contracts/create/${uuid}`,
  ContractListPage: ({ status }) => `/contracts/${status}`,
  ContractDetailsPage: ({ uuid }) => `/contracts/${uuid}`,
  ContractEditDetailsPage: ({ uuid }) => `/contracts/${uuid}/edit`,
  QboPage: () => '/integrations/qbo',
  SageIntacctPage: () => '/integrations/sage-intacct',
  Dynamics365BCPage: () => '/integrations/dynamics-365-business-central',
  DashboardPage: () => '/',
  IntegrationsPage: () => '/integrations',
  LegacyNetsuiteSyncLogsPage: () => '/integrations/netsuite',
  LegacyQuickBooksDesktopPage: () => '/#/integrations/quickbooks',
  QuickBooksDesktopSettingsPage: ({ tab }) =>
    `/integrations/quickbooks/settings/${tab}`,
  QuickBooksDesktopFieldMappingPage: ({ model }) =>
    `/integrations/quickbooks/settings/field-mapping/${model}`,
  QuickBooksDesktopAccountsPage: ({ status }) =>
    `/integrations/quickbooks/accounts/${status}`,
  QuickBooksDesktopVendorsPage: ({ status }) =>
    `/integrations/quickbooks/vendors/${status}`,
  QuickBooksDesktopExportsPage: ({ resource, status }) =>
    `/integrations/quickbooks/${resource}/${status}`,
  SlackIntegrationPage: () => '/integrations/settings/slack',
  NetsuitePage: () => '/integrations/netsuite',
  NetsuiteBillsPage: () => '/integrations/netsuite/bills',
  OrderStatusPage: ({ status }) => `/orders/requests/${status}`,
  OrderDetailsPage: ({ uuid, tab }) =>
    tab ? `/orders/${uuid}/${tab}` : `/orders/${uuid}`,
  LegacyOrderRequestPage: () => '/#/orders/request',
  OrderApprovalPage: ({ status }) => `/#/orders/approvals/${status}`,
  OrderApprovalsPage: ({ status }) => `/orders/approvals/${status}`,
  PaymentPayBillsPage: ({ status }) => `/payments/pay-bills/${status}`,
  PaymentPreparePage: ({ uuid }) => `/payments/prepare/${uuid}`,
  PaymentListPage: () => '/payments',
  PaymentDetailsPage: ({ uuid }) => `/payments/${uuid}`,
  PaymentApprovalListPage: ({ status }) => `/payments/approvals/${status}`,
  PaymentApprovalDetailsPage: ({ uuid }) => `/payments/approvals/${uuid}`,
  PaymentManagePayeesPage: <
    T extends PaymentManagePayeesPageTab = PaymentManagePayeesPageTab,
  >({
    type,
    id,
    tab,
  }: PaymentManagePayeesPageParams<T>) =>
    `/payments/manage-payees/${type}/${id}/${tab || ''}`,
  FinancialAccountPage: () => `/financial-account`,
  PaymentFinancialAccountPage: () => `/payments/financial-account`,
  ProcurementPage: () => '/procurement',
  ProcurifyApiPage: () => '/integrations/api',
  PurchaseOrderListPage: ({ status }) => `/purchase-orders/${status}`,
  PurchaseOrderCreatePoPage: ({ vendorId }) =>
    `/purchase-orders/create/${vendorId}`,
  PurchaseOrderDetailsPage: ({ uuid, tab }) =>
    `/purchase-orders/${uuid}${tab ? `/${tab}` : ``}`,
  PurchaseOrderDetailsPreviousVersionPage: ({ uuid, version }) =>
    `/purchase-orders/${uuid}/version/${version}`,
  PurchaseOrderRevisePoPage: ({ uuid }) => `/purchase-orders/${uuid}/revise`,
  PurchaseOrderDetailEmailLogPage: ({ uuid }) =>
    `/#/purchase/order/details/${uuid}/email-log`,
  PurchaseOrderCreate: () => `/#/purchase/order/create`,
  PunchOutSuppliersPage: ({ supplier }) => `/settings/punchout/${supplier}`,
  PunchOutGroupPage: ({ supplier, group }) =>
    `/settings/punchout/${supplier}/group/${group}`,
  PunchOutGroupConnectionLogsPage: ({ supplier, groupId }) =>
    `/settings/punchout/${supplier}/group/${groupId}/connection-logs`,
  ReceiveDetailsPage: ({ uuid }) => `/receive/${uuid}`,
  ReceiveListPage: ({ status }) => `/receive/${status}`,
  ReceivingSummaryPage: () => '/reports/receiving',
  OrdersByVendorReportPage: () => '/reports/orders/vendor',
  OrdersByVendorReportDetailsPage: ({ id }) => `/reports/orders/vendor/${id}`,
  OrdersByUserReportPage: () => '/reports/orders/user',
  OrdersByUserReportDetailsPage: ({ id }) => `/reports/orders/user/${id}`,
  OrdersBySecondaryCategoryReportPage: () => '/reports/orders/department',
  OrdersBySecondaryCategoryReportDetailsPage: ({ id }) =>
    `/reports/orders/department/${id}`,
  ExpensesBySecondaryCategoryReportPage: () => '/reports/expenses/department',
  ExpensesBySecondaryCategoryReportDetailsPage: ({ id }) =>
    `/reports/expenses/department/${id}`,
  ReportExportHistoryPage: () => '/reports/export-history',
  OverallSpendReportPage: ({ type }) => `/reports/overall-spend/${type}`,
  DiscoverMorePage: ({ tab } = {}) =>
    tab ? `/discover-more/${tab}` : '/discover-more',
  SettingsPage: () => '/settings',
  CatalogItemAccessPage: ({ tab }) => `/settings/permissions/${tab}`,
  CustomizeUnitTypesPage: () => '/settings/units',
  CustomizeOrderFormPage: () => '/settings/customfields-rfo',
  CustomizePurchaseOrderFormPage: ({ tab } = {}) =>
    tab ? `/settings/customfields-po/${tab}` : '/settings/customfields-po',
  AccountCodeCorrectionPage: ({ tab }) => `/settings/approved-requests/${tab}`,
  ClosingMonthsPage: () => '/settings/closing-month',
  ManagePaymentAccountsPage: ({ status }) => `/settings/financials/${status}`,
  SettingsBillFormPage: () => '/settings/customize-bill-form',
  SettingsExpenseFormPage: ({ tab } = {}) =>
    tab
      ? `/settings/customize-expense-form/${tab}`
      : '/settings/customize-expense-form',
  MainCustomFieldSetupPage: ({ tab } = {}) =>
    tab
      ? `/settings/custom-fields-setup/${tab}`
      : '/settings/custom-fields-setup',
  MainCustomFieldDetailPage: ({ tab, id }) =>
    `/settings/custom-fields-setup/${tab}/${id}`,
  SettingsManageFinancialAccountPage: () =>
    '/settings/manage-financial-account',
  SettingsManageBankAccountsPage: () => '/settings/manage-bank-accounts',
  PrimaryCategoryConfigListPage: ({ tab }) => `/settings/locations/${tab}`,
  PrimaryCategoryConfigDetailsPage: ({ id, tab }) =>
    `/settings/locations/${id}/${tab}`,
  LegalEntityListPage: ({ tab }) => `/settings/entities/${tab}`,
  LegalEntityDetailsPage: ({ uuid }) => `/settings/entities/${uuid}`,
  BudgetCategoryManagementRouter: () => '/settings/budget-categories',
  BudgetCategoryPage: () => '/settings/budget-categories',
  BudgetCategoryCreatePage: () => '/settings/budget-categories/create',
  BudgetCategoryEditPage: ({ uuid }) =>
    `/settings/budget-categories/edit/${uuid}`,
  BudgetsManagementRouter: () => '/settings/budgets',
  BudgetsPage: () => '/settings/budgets',
  BudgetsCreatePage: () => '/settings/budgets/create',
  BudgetsEditPage: ({ uuid }) => `/settings/budgets/edit/${uuid}`,
  SpendingCardApprovalListPage: ({ tab }) => `/spending-card/approvals/${tab}`,
  SpendingCardApproveFundRequestPage: ({ uuid }) =>
    `/spending-card/approvals/fund/${uuid}`,
  SpendingCardApproveVirtualCardRequestPage: ({ uuid }) =>
    `/spending-card/approvals/virtual-card/${uuid}`,
  SpendingCardCustomFieldsSettingsPage: () =>
    `/settings/spending-card-custom-fields`,
  SpendingCardManagementListPage: ({ tab }) =>
    `/spending-card/card-management/${tab}`,
  SpendingCardManagementDetailsPage: ({ card }) =>
    `/spending-card/card-management/${card}`,
  SpendingCardManagementFundDetailsPage: ({ card, fund }) =>
    `/spending-card/card-management/${card}/fund/${fund}`,
  SpendingCardReconcileTransactionsPage: () => `/spending-card/reconciliation`,
  SpendingCardRequestVirtualCardPage: () =>
    `/spending-card/virtual-cards/request`,
  SpendingCardVirtualCardsPage: ({ tab }) =>
    `/spending-card/virtual-cards/${tab}`,
  SpendingCardVirtualCardDetailsPage: ({ uuid }) =>
    `/spending-card/virtual-cards/card/${uuid}`,
  SpendingCardVirtualCardRequestDetailsPage: ({ uuid }) =>
    `/spending-card/virtual-cards/request/${uuid}`,
  SpendingCardVirtualCardTopUpDetailsPage: ({ card, uuid }) =>
    `/spending-card/virtual-cards/card/${card}/top-up-request/${uuid}`,
  SpendingCardPhysicalCardPage: ({ tab }) =>
    `/spending-card/physical-card/${tab}`,
  SpendingCardFundRequestDetailsPage: ({ uuid }) =>
    `/spending-card/physical-card/fund-request/${uuid}`,
  SpendingCardFundDetailsPage: ({ uuid }) =>
    `/spending-card/physical-card/fund/${uuid}`,
  SpendingCardCreateFundRequestPage: () =>
    `/spending-card/physical-card/request-fund`,
  SpendingCardTransactionListPage: ({ tab }) =>
    `/spending-card/transactions/${tab}`,
  UserProfilePage: ({ tab }) => `/profile/${tab}`,
  InstanceManagementPage: () => '/instance-management',
  SpendAnalyticsPage: () => '/analytics/spend-insights',
  VendorApprovalListPage: ({ status }) => `/vendor/approvals/${status}`,
  VendorApprovalDetailsPage: ({ id }) => `/vendor/approvals/${id}`,
  SettingsSupportAccessPage: () => '/settings/support-access',
  ContractSettingsPage: () => '/settings/contracts',
  SettingsMFAPreferencesPage: () => '/settings/mfa',
  SettingsManageDateFormatPage: () => '/settings/manage-date-format',
  SettingsReportAccessPage: () => '/settings/report-access',
  ExportDataPage: () => '/settings/export-data',
  RequestedExportsPage: ({ exportType }) =>
    `/settings/export-data/requested/${exportType}`,
  SettingsApprovalRoutingPage: ({ tab }) => `/settings/approval-routing/${tab}`,
  ExpenseApprovalsPage: ({ status }) => `/expenses/approvals/${status}`,
  ExpenseStatusPage: ({ status }) => `/expenses/requests/${status}`,
  TravelRequestPage: () => `/travels/request`,
  TravelStatusPage: ({ status }) => `/travels/requests/${status}`,
  TravelApprovalsPage: ({ status }) => `/travels/approvals/${status}`,
  TravelDetailsPage: ({ uuid }) => `/travels/${uuid}`,
  TravelEditPage: ({ uuid }) => `/travels/${uuid}/edit`,
  ExpenseDetailPage: ({ uuid }) => `/expenses/${uuid}`,
  ExpenseEditPage: ({ uuid }) => `/expenses/${uuid}/edit`,
  ExpenseRevisePage: ({ uuid }) => `/expenses/${uuid}/revise`,
  ExpenseRequestPage: () => `/expenses/request`,
  NoPermissionsPage: () => `/permissions-pending`,
  UserDetailsPage: ({ userId }) => `/settings/users/${userId}`,
  SettingsManageUsersPage: ({ status }) => `/settings/users/list/${status}`,
  LegacyApprovalDelegationHistoryPage: () =>
    `/#/settings/user/delegation-history`,
  SettingsManageRolesPermissionsPage: () => `/settings/roles-permissions`,
  LegacyUserCSVExportHistoryPage: () => '/#/settings/exportcsv/requested/users',
  LegacySpendingCardTransactionsCSVExportHistoryPage: () =>
    '/#/settings/exportcsv/requested/spending_card_transactions',
  UnbilledExpenseItemsPage: ({ status }) =>
    `/unbilled/expenses/${status || 'pending'}`,
  LegacyUnbilledExpenseItemsPage: () => '/#/ap/unbilled/expenses',
  OrderRequestPage: () => `/orders/request`,
  CardholderAccountPage: () => `/spending-card/cardholder`,
  CardholderAccountEditPage: () => `/spending-card/cardholder/edit`,
  CardholderAccountSetupPage: () => `/spending-card/cardholder/setup`,
  CardholderAccountVerifyPage: () => `/spending-card/cardholder/verify`,
  TransactionReconciliationPage: ({ tab }) =>
    `/spending-card/reconciliation/${tab}`,
  CardManagementPage: ({ status }) => `/spending-card/manage-cards/${status}`,
  CardManagementDetailsPage: ({ id, fundStatus }) =>
    `/spending-card/manage-cards/card/${id}/funds/${fundStatus}`,
  CardManagementFundDetailsPage: ({ id, fundId }) =>
    `/spending-card/manage-cards/card/${id}/fund/${fundId}`,
  RequestVirtualCardPage: () => `/spending-card/virtual-cards/request-card`,
  IssuingRequestVirtualCardPage: () =>
    `/spending-card/virtual-cards/request-card/issuing`,
  VendorManagementPage: ({ id, tab }) => `/vendors/${id || ''}/${tab || ''}`,
  UnbilledItemsPage: ({ status }) => `/unbilled/items/${status || 'active'}`,
}
